import * as React from 'react';
import {
    CreativeClickProps,
    logCreativeClick,
    logCreativeLoad,
} from '../../utils/logger';
import { TImprovedContentMarketing } from '../../../shared/types/contentMarketing';
import { useEffect, useMemo, useState } from 'react';
import { getConfig } from '../../config';
import SponsorHeader from './components/sponsor-header';

type TUsePulseLoadAndClick = {
    placementId: string;
    contentMarketing: TImprovedContentMarketing;
    isMultipleAds: boolean;
    multiAdIndex: number;
};

const usePulseLoadAndClick = ({
    placementId: originalPlacementId,
    contentMarketing,
    isMultipleAds,
    multiAdIndex,
}: TUsePulseLoadAndClick): CreativeClickProps => {
    const [pulseInitiated, setPulseInitiated] = useState(false);
    const { vertical, subvertical } = useMemo(() => {
        const config = getConfig();
        return {
            vertical: config.vertical,
            subvertical: config.subvertical,
            deviceType: config.deviceType,
        };
    }, []);

    const { adType, elementType, placementId } = useMemo(() => {
        return {
            adType: 'Content',
            elementType: isMultipleAds
                ? multiAdIndex === 0
                    ? 'Split Left'
                    : 'Split Right'
                : undefined,
            placementId: isMultipleAds
                ? `${originalPlacementId}_split_${multiAdIndex + 1}`
                : originalPlacementId,
        };
    }, [isMultipleAds, multiAdIndex, originalPlacementId]);

    const creativeClickObject: CreativeClickProps = useMemo(() => {
        return {
            creativeId: contentMarketing.id,
            elementId: placementId,
            verticalName: vertical,
            subVertical: subvertical,
            targetId: contentMarketing.href,
            provider: contentMarketing.sponsorData.name,
            adType,
            elementType,
            adFormat: isMultipleAds ? 'Split-view' : 'Single-view',
        };
    }, [
        contentMarketing,
        vertical,
        subvertical,
        adType,
        elementType,
        isMultipleAds,
        placementId,
    ]);

    useEffect(() => {
        if (
            !pulseInitiated &&
            document &&
            originalPlacementId &&
            contentMarketing
        ) {
            logCreativeLoad({
                creativeId: contentMarketing.id,
                elementId: placementId,
                verticalName: vertical,
                subVertical: subvertical,
                elementType,
                adType,
                adFormat: isMultipleAds ? 'Split-view' : 'Single-view',
            });
            setPulseInitiated(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placementId, pulseInitiated]);

    return creativeClickObject;
};

type TContentMarketingElement = {
    placementId: string;
    multiAdIndex?: number;
    contentMarketing: TImprovedContentMarketing;
    children: React.ReactNode;
    isMultipleAds?: boolean;
};

export const ContentMarketingElement = ({
    placementId,
    contentMarketing,
    children,
    isMultipleAds = false,
    multiAdIndex = undefined,
}: TContentMarketingElement) => {
    const creativeClickObject = usePulseLoadAndClick({
        placementId,
        contentMarketing,
        isMultipleAds,
        multiAdIndex,
    });

    const handleClickEvent = () => {
        if (placementId) {
            logCreativeClick(creativeClickObject);
        }
    };

    return (
        <article
            onClick={handleClickEvent}
            className={isMultipleAds ? 'w-full h-full' : 'col-span-full'}
            id={creativeClickObject?.elementId || placementId}
        >
            {children}
        </article>
    );
};

/**
 *
 * @param children
 * @param placementId
 * @param contentMarketingList
 * @param containerClassName
 * @param flexGap
 * @constructor
 * @return a list of elements with a render props which returns contentMarketing and index
 */
export const MultiWrapper = ({
    children,
    placementId,
    contentMarketingList,
    containerClassName = 'col-span-full',
    flexGap = 'gap-8',
}) => {
    const { deviceType, vertical } = getConfig();

    const carouselStyle =
        vertical === 'realestate'
            ? {
                  width: `calc(100vw)`,
                  marginLeft: -22,
                  paddingLeft: 16,
                  paddingRight: 22,
              }
            : {
                  width: 'calc(100vw + 16px)',
                  marginLeft: -32,
                  paddingLeft: 32,
                  paddingRight: 32,
              };
    if (deviceType === 'mobile') {
        return (
            <div className={containerClassName}>
                {Array.isArray(contentMarketingList) && (
                    <SponsorHeader contentMarketing={contentMarketingList[0]} />
                )}
                <div
                    className={'cm-carousel-container flex flex-row' + flexGap}
                    style={carouselStyle}
                >
                    {contentMarketingList.map(
                        (
                            contentMarketing: TImprovedContentMarketing,
                            i: number,
                        ) => (
                            <div
                                key={i}
                                className={
                                    'cm-carousel-child flex-auto w-2/4 m-4'
                                }
                            >
                                <div className={'h-full'}>
                                    <ContentMarketingElement
                                        key={contentMarketing.id}
                                        placementId={placementId}
                                        contentMarketing={contentMarketing}
                                        multiAdIndex={i}
                                        isMultipleAds={true}
                                    >
                                        <div
                                            data-testid={'cm-carousel-block'}
                                            className={'flex flex-col h-full'}
                                            style={{ width: '70vw' }}
                                        >
                                            {children(contentMarketing, i)}
                                        </div>
                                    </ContentMarketingElement>
                                </div>
                            </div>
                        ),
                    )}
                </div>
            </div>
        );
    }
    return (
        <div className={containerClassName}>
            {Array.isArray(contentMarketingList) && (
                <SponsorHeader contentMarketing={contentMarketingList[0]} />
            )}
            <div className={'flex flex-row ' + flexGap}>
                {contentMarketingList.map(
                    (
                        contentMarketing: TImprovedContentMarketing,
                        i: number,
                    ) => (
                        <div key={i} className={'flex-auto w-2/4'}>
                            <div className={'h-full'}>
                                <ContentMarketingElement
                                    key={contentMarketing.id}
                                    placementId={placementId}
                                    contentMarketing={contentMarketing}
                                    multiAdIndex={i}
                                    isMultipleAds={true}
                                >
                                    {children(contentMarketing, i)}
                                </ContentMarketingElement>
                            </div>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};
